<template>
  <v-card
    outlined
    class="d-flex pa-4 my-2"
    :data-recognition-id="recognition._id"
    :class="{ highlighted: isHighlighted }"
  >
    <div class="me-3 mt-1 d-flex">
      <UserAvatar
        size="32px"
        fontSize="10px"
        data-testid="recognitions-component-userAvatar"
        :account="recognition.createdBy"
      />

      <NumberAvatar
        v-if="hasMoreThanOneAccount"
        size="32px"
        class="ml-n2 avatar"
        fontSize="10px"
        data-testid="recognitions-component-numberAvatar"
        :number="recognition.accounts.length"
      />
      <UserAvatar
        v-else
        size="32px"
        class="ml-n2 avatar"
        fontSize="10px"
        data-testid="recognitions-component-userAvatar"
        :account="firstAccount"
      />
    </div>
    <div class="d-flex flex-column flex-grow-1">
      <div class="d-flex justify-space-between">
        <p
          class="ma-0 pa-0 subtitle-2 font-weight-600 blue-grey--text text--darken-3"
          data-testid="recognitions-text-recognitionTitle"
        >
          {{
            $t('recognitions.given_to_received', {
              given: getShortenedName(recognition.createdBy),
              received: getReceivedText,
            })
          }}
        </p>
        <v-menu v-if="!hideActions">
          <template v-slot:activator="{ on }">
            <v-btn
              v-if="canEdit || canDelete"
              icon
              color="blue-grey"
              small
              v-on="on"
              data-testid="recognitions-button-contextualMenu"
            >
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list nav dense>
            <v-list-item
              v-for="option in options"
              class="caption"
              :data-testid="`recognitions-listItem-${option.id}`"
              :key="option.id"
              @click="handleClick(option.id)"
            >
              <v-list-item-title>
                {{ option.text }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
      <p
        class="ma-0 pa-0 mt-0 caption blue-grey--text text--darken-1"
        data-testid="recognitions-text-createdAt"
      >
        {{ humanDate(recognition.createdAt) }}
        <span v-if="recognition.isEdited" data-testid="recognitions-text-edited"
          >- {{ $t('common.edited') }}</span
        >
      </p>
      <div
        class="recognition-content"
        :style="viewComplete ? '' : 'max-height: 100px; overflow: hidden;'"
      >
        <p
          data-testid="recognitions-text-recognitionContent"
          class="ma-0 pa-0 body-2 mt-2  blue-grey--text text--darken-3"
          style="white-space: break-spaces;"
          :id="recognition.id"
        >
          <template v-for="(chunk, i) in contentChunks">
            <UserSummaryPopover
              v-if="REGEXES.MENTION.test(chunk)"
              :key="chunk + i"
              :account="getAccountFromMention(chunk)"
            >
              <span
                class="primary--text"
                data-testid="recognitions-text-mention"
                @click="goToProfile(getAccountFromMention(chunk))"
                >{{ `${getFullName(getAccountFromMention(chunk))} ` }}</span
              >
            </UserSummaryPopover>
            <template v-else>{{ `${chunk}` }}</template>
          </template>
        </p>
        <div
          class="gradient"
          data-testid="recognitions-div-gradient"
          v-if="!viewComplete && recognitionExcedsLength"
        />
      </div>
      <div class="d-flex mt-3 mb-0" data-testid="recognitions-div-actions">
        <button
          v-if="isTranslationEnabled"
          class="blue--text mr-3 caption cursor-pointer d-inline-block"
          data-testid="recognitions-button-translate"
          @click="handleTranslateClick"
        >
          {{ translationText }}
        </button>

        <button
          v-if="recognitionExcedsLength"
          class="blue--text caption cursor-pointer d-inline-block"
          data-testid="recognitions-button-viewMore"
          @click="viewComplete = !viewComplete"
        >
          {{ viewCompleteText }}
        </button>
      </div>
      <div
        class="d-flex align-center justify-end mr-3"
        data-testid="recognitions-button-like"
      >
        <v-icon
          v-if="isLikedByMe"
          class="cursor-pointer"
          color="primary"
          @click="handleUnlike"
          >mdi-heart</v-icon
        >
        <v-icon
          v-else
          class="cursor-pointer"
          color="blue-grey"
          @click="handleLike"
          >mdi-heart-outline</v-icon
        >
        <p class="mb-0 ml-2 subtitle2 blue-grey--text text--darken-2">
          {{ recognition.totalLikes }}
        </p>
      </div>
    </div>
    <DeleteConfirmationModal
      :text="$t('recognitions.delete_recognition_text')"
      :title="$t('recognitions.delete_recognition')"
      :confirmText="$t('common.button.delete')"
      :show="showDeleteModal"
      @cancel="showDeleteModal = false"
      @delete="$emit('delete')"
    />
    <RecognitionDialog
      v-if="showEditModal"
      :show="showEditModal"
      :recognition="recognition"
      :initialAccounts="recognitionAccountIds"
      @saved="
        showEditModal = false
        $emit('edit')
      "
      @close="showEditModal = false"
    />
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex'
import _ from 'lodash'

import LANGUAGES_DICTIONARY from '@/locales/dictionary.json'
import api from '@/api'
import access from '@/lib/access'

import userMixin from '@/mixins/userMixin.vue'
import { utils } from 'frontend-js-shared'
import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import UserAvatar from '@/components/UserAvatar.vue'
import NumberAvatar from '@/components/NumberAvatar.vue'
import UserSummaryPopover from '@/components/UserSummaryPopover.vue'
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'
import RecognitionDialog from '@/components/RecognitionDialog.vue'

const REGEXES = {
  SPACES_NOT_CONSIDERING_NEW_LINES: /(\s)/gm,
  MENTION: /@[0-9a-fA-F]{24}/,
  SPACED_MENTIONS: /@\s/g, // After translating a comment with mentions, the "@" character and the account id gets separated by Google Translate. We manually fix this using the "replace" method passing this regex as parameter.
}

export default {
  name: 'RecognitionsCard',

  mixins: [userMixin, mixin, trackerMixin],

  components: {
    UserAvatar,
    NumberAvatar,
    UserSummaryPopover,
    DeleteConfirmationModal,
    RecognitionDialog,
  },

  props: {
    recognition: { required: true, type: Object },
    isHighlighted: Boolean,
    hideActions: Boolean,
  },

  data() {
    return {
      REGEXES,
      isTranslating: false,
      translatedText: '',
      snip: 5,
      viewComplete: false,
      showEditModal: false,
      showDeleteModal: false,
    }
  },

  computed: {
    ...mapGetters([
      'language',
      'availableFeatures',
      'organizationId',
      'accountId',
      'account',
    ]),

    canEdit() {
      return this.accountId === this.recognition.createdBy._id
    },

    canDelete() {
      return this.canEdit || access.isOrganizationAdmin(this.account)
    },

    options() {
      return _.compact([
        this.canEdit && { id: 'edit', text: 'Edit' },
        this.canDelete && { id: 'delete', text: 'Delete' },
      ])
    },

    viewCompleteText() {
      return this.viewComplete
        ? this.$t('common.button.view_less')
        : this.$t('common.button.view_more')
    },

    recognitionExcedsLength() {
      return this.recognition.content.length > 200
    },

    recognitionLanguage() {
      return this.recognition?.language
    },

    isTranslated() {
      return Boolean(this.translatedText.length)
    },

    isLikedByMe() {
      return this.recognition.likedByMe
    },

    translationText() {
      return this.isTranslated
        ? this.$t('common.see_original_lang', {
            lang: this.$t(`languages.${this.getLanguageName()}`),
          })
        : this.$t('common.translate')
    },

    textToShow() {
      return this.translatedText.length
        ? this.translatedText
        : this.recognition.content
    },

    isTranslationEnabled() {
      if (
        !this.recognitionLanguage ||
        this.recognitionLanguage === this.language ||
        !LANGUAGES_DICTIONARY?.['primary-dialects'][this.recognitionLanguage]
      )
        return false

      return this.availableFeatures.includes('translation')
    },

    getReceivedText() {
      if (this.hasMoreThanOneAccount)
        return this.$t('recognitions.people_count', {
          count: this.recognition.accounts?.length,
        })
      return this.getShortenedName(this.firstAccount)
    },

    hasMoreThanOneAccount() {
      return this.recognition.accounts?.length > 1
    },

    firstAccount() {
      return this.recognition.accounts?.[0]
    },

    recognitionAccountIds() {
      return this.recognition.accounts?.map(account => account._id)
    },

    contentChunks() {
      return (
        this.textToShow
          ?.replace?.(REGEXES.SPACED_MENTIONS, '@')
          ?.split?.(REGEXES.SPACES_NOT_CONSIDERING_NEW_LINES) ?? []
      )
    },
  },

  methods: {
    humanDate(date) {
      return utils.dates.humanDate(date)
    },

    goToProfile(account) {
      this.trackRecognitionsNavigate({
        option: 'goToProfile',
      })

      this.navigateTo({
        name: 'profile',
        params: {
          organizationId: account?.organization,
          userId: account?._id,
        },
      })
    },

    handleEditClick() {
      this.showEditModal = true
      this.$track('recognitions.Option', this.$store, { type: 'edit' })
    },

    handleDeleteClick() {
      this.showDeleteModal = true
      this.$track('recognitions.Option', this.$store, { type: 'delete' })
    },

    handleClick(option) {
      switch (option) {
        case 'edit':
          return this.handleEditClick()
        case 'delete':
          return this.handleDeleteClick()
        default:
          return null
      }
    },

    async handleLike() {
      try {
        await api.recognition.like(this.organizationId, this.recognition._id)
        this.recognition.likedByMe = true
        this.recognition.totalLikes++
        this.$track('recognitions.Likes', this.$store, { type: 'like' })
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    async handleUnlike() {
      try {
        await api.recognition.unlike(this.organizationId, this.recognition._id)
        this.recognition.likedByMe = false
        this.recognition.totalLikes--
        this.$track('recognitions.Likes', this.$store, { type: 'unlike' })
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    async translate() {
      try {
        this.isTranslating = true
        const response = await api.translate.text(
          this.organizationId,
          this.recognition.content,
          this.language
        )

        this.translatedText = response.data.translatedText
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isTranslating = true
      }
    },

    handleTranslateClick() {
      if (this.isTranslated) return (this.translatedText = '')
      return this.translate()
    },

    getLanguageName() {
      const availableLanguages = this.getLanguagesForTranslationKeys()

      return (
        availableLanguages
          .find(lang => this.recognitionLanguage === lang.value)
          ?.name.toLowerCase() ??
        this.recognitionLanguage ??
        ''
      )
    },

    getAccountFromMention(mention) {
      const accountId = mention.slice(1)
      return (
        this.recognition.accounts.find(account => account._id === accountId) ??
        {}
      )
    },
  },
}
</script>

<style lang="scss" scoped>
.avatar {
  border: 1px solid white;
  height: 34px;
  border-radius: 17px;
}

.gradient {
  position: absolute;
  height: 60px;
  width: 100%;
  bottom: 0;
  background: linear-gradient(transparent, white);
}

.recognition-content {
  position: relative;
}
</style>
